package com.havn.app

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.tween
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.NavigationRail
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.runtime.State
import com.havn.app.core.di.AppContainer
import com.havn.app.core.billing.HavnBillingService
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnPushNavigation
import com.havn.app.core.designsystem.HavnSpacing
import com.havn.app.core.designsystem.HavnTheme
import com.havn.app.core.designsystem.havnIsWebShell
import com.havn.app.core.platform.HavnExternalActions
import com.havn.app.core.platform.havnIsTabletDevice
import com.havn.app.ui.theme.HavnThemeManager
import com.havn.app.ui.theme.HavnThemeType
import com.havn.app.ui.theme.LocalHavnThemeTokens
import com.havn.app.ui.theme.canUsePremiumThemes
import com.havn.app.core.localization.HavnLocalizationProvider
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.localization.localizedShortLabel
import com.havn.app.core.localization.localizedTitle
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.core.media.HavnPhotoPicker
import com.havn.app.core.navigation.HavnDestination
import com.havn.app.core.settings.LocalHavnUnitPreferences
import com.havn.app.core.supabase.HavnSupabaseConfig
import com.havn.app.features.agency.domain.HavnSubscriptionTier
import com.havn.app.features.agency.presentation.AgencyScreen
import com.havn.app.features.agency.presentation.AgencyViewModel
import com.havn.app.features.analytics.presentation.AnalyticsScreen
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import com.havn.app.features.auth.presentation.AuthScreen
import com.havn.app.features.auth.presentation.AuthViewModel
import com.havn.app.features.clients.presentation.ClientsScreen
import com.havn.app.features.clients.presentation.ClientsViewModel
import com.havn.app.features.dashboard.presentation.DashboardScreen
import com.havn.app.features.followups.presentation.FollowUpsScreen
import com.havn.app.features.followups.presentation.FollowUpsViewModel
import com.havn.app.features.leads.presentation.LeadsScreen
import com.havn.app.features.leads.presentation.LeadsViewModel
import com.havn.app.features.openhouses.presentation.OpenHousesScreen
import com.havn.app.features.openhouses.presentation.OpenHousesMode
import com.havn.app.features.openhouses.presentation.OpenHousesViewModel
import com.havn.app.features.openhouses.presentation.KioskBranding
import com.havn.app.features.notifications.domain.NotificationRepository
import com.havn.app.features.notifications.presentation.NotificationCoordinator
import com.havn.app.features.notifications.platform.HavnPushNotifications
import com.havn.app.features.properties.presentation.PropertiesScreen
import com.havn.app.features.properties.presentation.PropertiesViewModel
import com.havn.app.features.reports.presentation.SellerReportsScreen
import com.havn.app.features.reports.presentation.SellerReportsViewModel
import com.havn.app.features.settings.presentation.SettingsScreen
import com.havn.app.features.tasks.presentation.TasksScreen
import com.havn.app.features.tasks.presentation.TasksViewModel
import havn.composeapp.generated.resources.Res
import havn.composeapp.generated.resources.havn_auth_logo
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.painterResource

@Composable
fun HavnApp(
    navigationState: HavnNavigationState? = null,
    showSystemNavigation: Boolean = true,
    photoPicker: HavnPhotoPicker = HavnPhotoPicker.Unavailable,
    onSystemNavigationVisibleChanged: (Boolean) -> Unit = {},
    onKioskModeChanged: (Boolean) -> Unit = {}
) {
    val container = remember { AppContainer.default() }
    val themeType by container.themeManager.themeType.collectAsState()
    HavnTheme(themeType = themeType) {
        val language by container.languageRepository.language.collectAsState()
        val unitPreferences by container.unitPreferencesRepository.preferences.collectAsState()
        var showLaunchScreen by remember { mutableStateOf(!havnIsWebShell) }
        var launchMinimumElapsed by remember { mutableStateOf(havnIsWebShell) }
        var launchLoggedOutAllowed by remember { mutableStateOf(havnIsWebShell) }
        var launchExiting by remember { mutableStateOf(false) }
        LaunchedEffect(Unit) {
            if (!havnIsWebShell) {
                delay(1_000)
                launchMinimumElapsed = true
                delay(1_200)
                launchLoggedOutAllowed = true
            }
        }
        val authViewModel = remember { AuthViewModel(container.authRepository) }
        val propertiesViewModel = remember {
            PropertiesViewModel(
                container.propertyRepository,
                container.clientRepository,
                container.authRepository,
                container.unitPreferencesRepository,
                container.agencyRepository
            )
        }
        val openHousesViewModel = remember {
            OpenHousesViewModel(
                container.openHouseRepository,
                container.clientRepository,
                container.authRepository,
                container.agencyRepository
            )
        }
        val leadsViewModel = remember {
            LeadsViewModel(container.leadRepository, container.authRepository)
        }
        val clientsViewModel = remember {
            ClientsViewModel(container.clientRepository, container.authRepository)
        }
        val followUpsViewModel = remember {
            FollowUpsViewModel(
                followUpRepository = container.followUpRepository,
                leadRepository = container.leadRepository,
                aiFollowUpService = container.aiFollowUpService,
                authRepository = container.authRepository,
                languageRepository = container.languageRepository
            )
        }
        val sellerReportsViewModel = remember {
            SellerReportsViewModel(
                sellerReportRepository = container.sellerReportRepository,
                openHouseRepository = container.openHouseRepository,
                authRepository = container.authRepository
            )
        }
        val tasksViewModel = remember {
            TasksViewModel(
                taskRepository = container.taskRepository,
                authRepository = container.authRepository
            )
        }
        val agencyViewModel = remember {
            AgencyViewModel(container.agencyRepository, container.authRepository)
        }
        val notificationCoordinator = remember {
            NotificationCoordinator(container.authRepository, container.notificationRepository)
        }
        LaunchedEffect(notificationCoordinator, propertiesViewModel, openHousesViewModel) {
            propertiesViewModel.onPropertyCreated {
                notificationCoordinator.maybeRequestAfterFirstBusinessAction(hasAnyProperty = true, hasAnyOpenHouse = false)
            }
            openHousesViewModel.onOpenHouseScheduled {
                notificationCoordinator.maybeRequestAfterFirstBusinessAction(hasAnyProperty = false, hasAnyOpenHouse = true)
            }
        }
        val openHousesAppState by openHousesViewModel.uiState.collectAsState()
        LaunchedEffect(openHousesAppState.mode) {
            onKioskModeChanged(openHousesAppState.mode == OpenHousesMode.VisitorKiosk)
        }
        val agencyState by agencyViewModel.uiState.collectAsState()
        LaunchedEffect(agencyState.hasLoadedEntitlement, agencyState.entitlement.tier, themeType) {
            if (
                agencyState.hasLoadedEntitlement &&
                !canUsePremiumThemes(agencyState.entitlement.tier) &&
                themeType != HavnThemeType.OBSIDIAN_GOLD
            ) {
                container.themeManager.setTheme(HavnThemeType.OBSIDIAN_GOLD, canUsePremiumThemes = true)
            }
        }
        val effectiveAgencyContextId = agencyState.selectedAgencyId
        LaunchedEffect(agencyState.hasLoadedEntitlement, effectiveAgencyContextId) {
            if (!agencyState.hasLoadedEntitlement) return@LaunchedEffect
            propertiesViewModel.setAgencyContext(effectiveAgencyContextId)
            openHousesViewModel.setAgencyContext(effectiveAgencyContextId)
            clientsViewModel.setAgencyContext(effectiveAgencyContextId)
            leadsViewModel.setAgencyContext(effectiveAgencyContextId)
            tasksViewModel.setAgencyContext(effectiveAgencyContextId)
        }
        DisposableEffect(
            authViewModel,
            propertiesViewModel,
            openHousesViewModel,
            clientsViewModel,
            leadsViewModel,
            followUpsViewModel,
            sellerReportsViewModel,
            tasksViewModel,
            agencyViewModel,
            notificationCoordinator
        ) {
            onDispose {
                authViewModel.dispose()
                propertiesViewModel.dispose()
                openHousesViewModel.dispose()
                clientsViewModel.dispose()
                leadsViewModel.dispose()
                followUpsViewModel.dispose()
                sellerReportsViewModel.dispose()
                tasksViewModel.dispose()
                agencyViewModel.dispose()
                notificationCoordinator.dispose()
            }
        }
        val authUiState by authViewModel.uiState.collectAsState()
        LaunchedEffect(
            showLaunchScreen,
            launchMinimumElapsed,
            launchLoggedOutAllowed,
            launchExiting,
            authUiState.authState
        ) {
            if (!showLaunchScreen || launchExiting || havnIsWebShell) return@LaunchedEffect
            val canExitLaunch = when (authUiState.authState) {
                is AuthState.LoggedIn -> launchMinimumElapsed
                AuthState.LoggedOut,
                is AuthState.Error -> launchLoggedOutAllowed
                AuthState.Loading -> false
            }
            if (canExitLaunch) {
                launchExiting = true
            }
        }
        LaunchedEffect(launchExiting) {
            if (launchExiting) {
                delay(380)
                showLaunchScreen = false
            }
        }
        LaunchedEffect(authUiState.authState, showLaunchScreen, showSystemNavigation, openHousesAppState.mode) {
            onSystemNavigationVisibleChanged(
                (showSystemNavigation || navigationState != null) &&
                    !showLaunchScreen &&
                    authUiState.authState is AuthState.LoggedIn &&
                    openHousesAppState.mode != OpenHousesMode.VisitorKiosk
            )
        }
        var internalDestination by rememberSaveable { mutableStateOf(HavnDestination.Dashboard) }
        val externalDestination: State<HavnDestination>? =
            navigationState?.selectedDestination?.collectAsState(HavnDestination.Dashboard)
        val destination = externalDestination?.value ?: internalDestination
        val externalRootRequestCount: Int? =
            navigationState?.rootNavigationRequestCount?.collectAsState(0)?.value
        val onDestinationSelected: (HavnDestination) -> Unit = { nextDestination ->
            if (navigationState == null) {
                internalDestination = nextDestination
            } else {
                navigationState.selectDestination(nextDestination)
            }
        }
        val resetDestinationToRoot: (HavnDestination) -> Unit = { rootDestination ->
            resetDestinationToRoot(
                destination = rootDestination,
                propertiesViewModel = propertiesViewModel,
                openHousesViewModel = openHousesViewModel,
                leadsViewModel = leadsViewModel,
                clientsViewModel = clientsViewModel,
                followUpsViewModel = followUpsViewModel,
                sellerReportsViewModel = sellerReportsViewModel
            )
        }
        val onTabSelected: (HavnDestination) -> Unit = { nextDestination ->
            resetDestinationToRoot(nextDestination)
            onDestinationSelected(nextDestination)
        }

        if (externalRootRequestCount != null) {
            LaunchedEffect(externalRootRequestCount) {
                resetDestinationToRoot(destination)
            }
        }

        val focusManager = LocalFocusManager.current

        HavnLocalizationProvider(language = language) {
            androidx.compose.runtime.CompositionLocalProvider(LocalHavnUnitPreferences provides unitPreferences) {
            Box(
                Modifier
                    .fillMaxSize()
                    .pointerInput(Unit) {
                        detectTapGestures(onTap = { focusManager.clearFocus() })
                    }
            ) {
                when (authUiState.authState) {
                    AuthState.Loading -> AuthLoadingScreen()
                    AuthState.LoggedOut,
                    is AuthState.Error -> {
                        if (showLaunchScreen && !havnIsWebShell) {
                            AuthLoadingScreen()
                        } else {
                            AuthScreen(
                                uiState = authUiState,
                                onEmailChanged = authViewModel::onEmailChanged,
                                onOtpCodeChanged = authViewModel::onOtpCodeChanged,
                                onSendEmailOtp = authViewModel::sendEmailOtp,
                                onVerifyEmailOtp = authViewModel::verifyEmailOtp,
                                onSignInWithGoogle = authViewModel::signInWithGoogle,
                                onSignInWithApple = authViewModel::signInWithApple,
                                onShowWelcome = authViewModel::showWelcome,
                                onShowEmailEntry = authViewModel::showEmailEntry
                            )
                        }
                    }
                    is AuthState.LoggedIn -> AuthenticatedApp(
                        destination = destination,
                        authState = authUiState.authState,
                        showSystemNavigation = showSystemNavigation,
                        onDestinationSelected = onDestinationSelected,
                        onTabSelected = onTabSelected,
                        onLogout = authViewModel::logout,
                        propertiesViewModel = propertiesViewModel,
                        openHousesViewModel = openHousesViewModel,
                        leadsViewModel = leadsViewModel,
                        clientsViewModel = clientsViewModel,
                        followUpsViewModel = followUpsViewModel,
                        sellerReportsViewModel = sellerReportsViewModel,
                        tasksViewModel = tasksViewModel,
                        agencyViewModel = agencyViewModel,
                        photoPicker = photoPicker,
                        authRepository = container.authRepository,
                        languageRepository = container.languageRepository,
                        unitPreferencesRepository = container.unitPreferencesRepository,
                        kioskSecurityRepository = container.kioskSecurityRepository,
                        themeManager = container.themeManager,
                        notificationRepository = container.notificationRepository,
                        billingService = container.billingService
                    )
                }
                HavnEnvironmentBadge()
                if (showLaunchScreen) {
                    HavnLaunchScreen(exiting = launchExiting)
                }
            }
            }
        }
    }
}

@Composable
private fun BoxScope.HavnEnvironmentBadge() {
    if (HavnSupabaseConfig.environment != "sandbox") return
    Text(
        text = "SANDBOX",
        color = Color(0xFF111827),
        style = MaterialTheme.typography.labelSmall,
        fontWeight = FontWeight.Bold,
        modifier = Modifier
            .align(Alignment.TopCenter)
            .padding(top = 10.dp)
            .clip(RoundedCornerShape(6.dp))
            .background(Color(0xFFF4C542))
            .border(1.dp, Color(0xFF8A6A12), RoundedCornerShape(6.dp))
            .padding(horizontal = 10.dp, vertical = 4.dp)
    )
}

@Composable
private fun HavnLaunchScreen(exiting: Boolean) {
    val tokens = LocalHavnThemeTokens.current
    val overlayVisibility = remember { Animatable(1f) }
    val contentVisibility = remember { Animatable(0f) }
    LaunchedEffect(Unit) {
        contentVisibility.animateTo(
            targetValue = 1f,
            animationSpec = tween(durationMillis = 420, easing = FastOutSlowInEasing)
        )
    }
    LaunchedEffect(exiting) {
        if (!exiting) return@LaunchedEffect
        overlayVisibility.animateTo(
            targetValue = 0f,
            animationSpec = tween(durationMillis = 360, easing = FastOutSlowInEasing)
        )
    }
    val overlayValue = overlayVisibility.value
    val contentValue = contentVisibility.value
    Box(
        modifier = Modifier
            .fillMaxSize()
            .alpha(overlayValue)
            .background(
                Brush.verticalGradient(
                    listOf(tokens.gradientStart, tokens.background, tokens.gradientEnd)
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)
        ) {
            Image(
                painter = painterResource(Res.drawable.havn_auth_logo),
                contentDescription = null,
                modifier = Modifier
                    .size(108.dp)
                    .alpha(contentValue)
                    .scale(0.92f + (contentValue * 0.08f))
                    .clip(RoundedCornerShape(24.dp))
            )
            Text(
                text = "HAVN Real Estate",
                color = tokens.primaryAccent,
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.alpha(contentValue)
            )
        }
    }
}

@Composable
private fun AuthenticatedApp(
    destination: HavnDestination,
    authState: AuthState,
    showSystemNavigation: Boolean,
    onDestinationSelected: (HavnDestination) -> Unit,
    onTabSelected: (HavnDestination) -> Unit,
    onLogout: () -> Unit,
    propertiesViewModel: PropertiesViewModel,
    openHousesViewModel: OpenHousesViewModel,
    leadsViewModel: LeadsViewModel,
    clientsViewModel: ClientsViewModel,
    followUpsViewModel: FollowUpsViewModel,
    sellerReportsViewModel: SellerReportsViewModel,
    tasksViewModel: TasksViewModel,
    agencyViewModel: AgencyViewModel,
    photoPicker: HavnPhotoPicker,
    authRepository: AuthRepository,
    languageRepository: com.havn.app.core.localization.HavnLanguageRepository,
    unitPreferencesRepository: com.havn.app.core.settings.HavnUnitPreferencesRepository,
    kioskSecurityRepository: com.havn.app.core.settings.HavnKioskSecurityRepository,
    themeManager: HavnThemeManager,
    notificationRepository: NotificationRepository,
    billingService: HavnBillingService
) {
    BoxWithConstraints(Modifier.fillMaxSize()) {
        if (!showSystemNavigation) {
            HavnScaffold(
                destination = destination,
                authState = authState,
                showBottomBar = false,
                useSafeDrawingInsets = false,
                onDestinationSelected = onDestinationSelected,
                onTabSelected = onTabSelected,
                onLogout = onLogout,
                propertiesViewModel = propertiesViewModel,
                openHousesViewModel = openHousesViewModel,
                leadsViewModel = leadsViewModel,
                clientsViewModel = clientsViewModel,
                followUpsViewModel = followUpsViewModel,
                sellerReportsViewModel = sellerReportsViewModel,
                tasksViewModel = tasksViewModel,
                agencyViewModel = agencyViewModel,
                photoPicker = photoPicker,
                authRepository = authRepository,
                languageRepository = languageRepository,
                unitPreferencesRepository = unitPreferencesRepository,
                kioskSecurityRepository = kioskSecurityRepository,
                themeManager = themeManager,
                notificationRepository = notificationRepository,
                billingService = billingService
            )
        } else {
            HavnScaffold(
                destination = destination,
                authState = authState,
                showBottomBar = true,
                useSafeDrawingInsets = true,
                onDestinationSelected = onDestinationSelected,
                onTabSelected = onTabSelected,
                onLogout = onLogout,
                propertiesViewModel = propertiesViewModel,
                openHousesViewModel = openHousesViewModel,
                leadsViewModel = leadsViewModel,
                clientsViewModel = clientsViewModel,
                followUpsViewModel = followUpsViewModel,
                sellerReportsViewModel = sellerReportsViewModel,
                tasksViewModel = tasksViewModel,
                agencyViewModel = agencyViewModel,
                photoPicker = photoPicker,
                authRepository = authRepository,
                languageRepository = languageRepository,
                unitPreferencesRepository = unitPreferencesRepository,
                kioskSecurityRepository = kioskSecurityRepository,
                themeManager = themeManager,
                notificationRepository = notificationRepository,
                billingService = billingService
            )
        }
    }
}

@Composable
private fun AuthLoadingScreen() {
    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        CircularProgressIndicator()
    }
}

private data class HavnSystemNavigationItem(
    val destination: HavnDestination,
    val label: String? = null,
    val icon: HavnIconKey? = null
)

private val havnSystemNavigationItems = listOf(
    HavnSystemNavigationItem(HavnDestination.Dashboard, icon = HavnIconKey.TodayGrid),
    HavnSystemNavigationItem(HavnDestination.Properties, icon = HavnIconKey.Home),
    HavnSystemNavigationItem(HavnDestination.Clients, icon = HavnIconKey.ContactCard),
    HavnSystemNavigationItem(HavnDestination.OpenHouses, icon = HavnIconKey.CalendarClock),
    HavnSystemNavigationItem(HavnDestination.Leads)
)

@Composable
private fun HavnScaffold(
    destination: HavnDestination,
    authState: AuthState,
    showBottomBar: Boolean,
    useSafeDrawingInsets: Boolean,
    onDestinationSelected: (HavnDestination) -> Unit,
    onTabSelected: (HavnDestination) -> Unit,
    onLogout: () -> Unit,
    propertiesViewModel: PropertiesViewModel,
    openHousesViewModel: OpenHousesViewModel,
    leadsViewModel: LeadsViewModel,
    clientsViewModel: ClientsViewModel,
    followUpsViewModel: FollowUpsViewModel,
    sellerReportsViewModel: SellerReportsViewModel,
    tasksViewModel: TasksViewModel,
    agencyViewModel: AgencyViewModel,
    photoPicker: HavnPhotoPicker,
    authRepository: AuthRepository,
    languageRepository: com.havn.app.core.localization.HavnLanguageRepository,
    unitPreferencesRepository: com.havn.app.core.settings.HavnUnitPreferencesRepository,
    kioskSecurityRepository: com.havn.app.core.settings.HavnKioskSecurityRepository,
    themeManager: HavnThemeManager,
    notificationRepository: NotificationRepository,
    billingService: HavnBillingService
) {
    val openHousesState by openHousesViewModel.uiState.collectAsState()
    val agencyState by agencyViewModel.uiState.collectAsState()
    var leadReturnOpeningId by remember { mutableStateOf<String?>(null) }
    var contactReturnClientId by remember { mutableStateOf<String?>(null) }
    var contactReturnLabel by remember { mutableStateOf<String?>(null) }
    var openingReturnToToday by remember { mutableStateOf(false) }
    var openSettingsPurchase by remember { mutableStateOf(false) }
    var agentProfileRefreshToken by remember { mutableStateOf(0) }
    val pendingDeepLink by HavnPushNotifications.deepLinks.collectAsState()
    val canUseProModules = agencyState.entitlement.isProAgent
    val rootTabSelected: (HavnDestination) -> Unit = { nextDestination ->
        if (nextDestination.isProModule && !canUseProModules) {
            openSettingsPurchase = true
            onTabSelected(HavnDestination.Settings)
        } else {
            leadReturnOpeningId = null
            contactReturnClientId = null
            contactReturnLabel = null
            openingReturnToToday = false
            openSettingsPurchase = false
            onTabSelected(nextDestination)
        }
    }
    LaunchedEffect(pendingDeepLink) {
        val link = HavnPushNotifications.consumePendingDeepLink() ?: return@LaunchedEffect
        routeHavnDeepLink(
            link = link,
            propertiesViewModel = propertiesViewModel,
            openHousesViewModel = openHousesViewModel,
            leadsViewModel = leadsViewModel,
            sellerReportsViewModel = sellerReportsViewModel,
            onDestinationSelected = onDestinationSelected,
            onOpenSubscription = {
                openSettingsPurchase = true
                onDestinationSelected(HavnDestination.Settings)
            }
        )
    }
    val effectiveShowBottomBar = showBottomBar &&
        !(destination == HavnDestination.OpenHouses && openHousesState.mode == OpenHousesMode.VisitorKiosk)

    BoxWithConstraints(Modifier.fillMaxSize()) {
        val useWideSideMenu = effectiveShowBottomBar &&
            (havnIsWebShell || havnIsTabletDevice() || maxWidth >= 500.dp)
        val useNavigationRail = false
        Scaffold(
            contentWindowInsets = if (useSafeDrawingInsets) WindowInsets.safeDrawing else WindowInsets(0.dp),
            containerColor = LocalHavnThemeTokens.current.background,
            bottomBar = {
                if (effectiveShowBottomBar && !havnIsWebShell && !useWideSideMenu && !useNavigationRail) {
                    HavnBottomNavigation(destination, rootTabSelected)
                }
            }
        ) { paddingValues ->
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues)
            ) {
                if (useWideSideMenu) {
                    HavnWideSideMenu(
                        selected = destination,
                        showAgency = agencyState.entitlement.isProAgent,
                        showProModules = canUseProModules,
                        authState = authState,
                        authRepository = authRepository,
                        profileRefreshToken = agentProfileRefreshToken,
                        planLabel = agencyState.entitlement.tier.displayLabel,
                        onDestinationSelected = rootTabSelected,
                        onUtilitySelected = rootTabSelected,
                        onUpgradePlan = {
                            leadReturnOpeningId = null
                            contactReturnClientId = null
                            contactReturnLabel = null
                            openingReturnToToday = false
                            openSettingsPurchase = true
                            onDestinationSelected(HavnDestination.Settings)
                        }
                    )
                } else if (useNavigationRail) {
                    HavnNavigationRail(
                        selected = destination,
                        onDestinationSelected = rootTabSelected
                    )
                }
                Box(modifier = Modifier.fillMaxSize()) {
                    HavnPushNavigation(
                        targetState = destination,
                        depthFor = HavnDestination::navigationDepth,
                        modifier = Modifier.fillMaxSize()
                    ) { visibleDestination ->
                        HavnScreen(
                            visibleDestination,
                            onLogout,
                            propertiesViewModel,
                            openHousesViewModel,
                            leadsViewModel,
                            clientsViewModel,
                            followUpsViewModel,
                            sellerReportsViewModel,
                            tasksViewModel,
                            agencyViewModel,
                            photoPicker,
                            authRepository,
                            languageRepository,
                            unitPreferencesRepository,
                            kioskSecurityRepository,
                            themeManager,
                            notificationRepository,
                            billingService,
                            canUseProModules = canUseProModules,
                            agentProfileRefreshToken = agentProfileRefreshToken,
                            onAgentProfileChanged = { agentProfileRefreshToken++ },
                            leadReturnOpeningId = leadReturnOpeningId,
                            onLeadReturnOpeningChanged = { leadReturnOpeningId = it },
                            contactReturnClientId = contactReturnClientId,
                            contactReturnLabel = contactReturnLabel,
                            onContactReturnChanged = { clientId, label ->
                                contactReturnClientId = clientId
                                contactReturnLabel = label
                            },
                            openingReturnToToday = openingReturnToToday,
                            onOpeningReturnToTodayChanged = { openingReturnToToday = it },
                            onDestinationSelected = onDestinationSelected,
                            showDashboardHeaderActions = !useNavigationRail && !useWideSideMenu,
                            openSettingsPurchase = openSettingsPurchase,
                            onSettingsPurchaseConsumed = { openSettingsPurchase = false },
                            onOpenSubscription = {
                                leadReturnOpeningId = null
                                contactReturnClientId = null
                                contactReturnLabel = null
                                openingReturnToToday = false
                                openSettingsPurchase = true
                                onDestinationSelected(HavnDestination.Settings)
                            }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun HavnBottomNavigation(
    selected: HavnDestination,
    onDestinationSelected: (HavnDestination) -> Unit
) {
    val strings = LocalHavnStrings.current
    val colors = LocalHavnThemeTokens.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .navigationBarsPadding()
            .padding(start = 18.dp, end = 18.dp, top = 6.dp, bottom = 12.dp),
        contentAlignment = Alignment.Center
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(74.dp)
                .clip(RoundedCornerShape(37.dp))
                .background(colors.surface.copy(alpha = 0.96f))
                .border(BorderStroke(1.dp, colors.border.copy(alpha = 0.56f)), RoundedCornerShape(37.dp))
                .padding(horizontal = 10.dp, vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            havnSystemNavigationItems.forEach { item ->
                val destination = item.destination
                val label = item.label ?: destination.localizedShortLabel(strings)
                val icon = item.icon ?: destination.icon
                HavnNavigationTile(
                    selected = selected == destination,
                    label = label,
                    icon = icon,
                    contentDescription = destination.localizedTitle(strings),
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxHeight()
                        .padding(horizontal = 2.dp),
                    onClick = { onDestinationSelected(destination) }
                )
            }
        }
    }
}

@Composable
private fun HavnNavigationRail(
    selected: HavnDestination,
    onDestinationSelected: (HavnDestination) -> Unit
) {
    val strings = LocalHavnStrings.current
    val colors = LocalHavnThemeTokens.current
    NavigationRail(
        modifier = Modifier
            .padding(start = 12.dp, top = 12.dp, bottom = 12.dp)
            .width(98.dp)
            .fillMaxHeight()
            .clip(RoundedCornerShape(34.dp))
            .border(BorderStroke(1.dp, colors.border.copy(alpha = 0.56f)), RoundedCornerShape(34.dp)),
        containerColor = colors.surface.copy(alpha = 0.96f),
        contentColor = colors.primaryText
    ) {
        Spacer(Modifier.height(HavnSpacing.md))
        havnSystemNavigationItems.forEach { item ->
            val destination = item.destination
            val label = item.label ?: destination.localizedShortLabel(strings)
            val icon = item.icon ?: destination.icon
            HavnNavigationTile(
                selected = selected == destination,
                label = label,
                icon = icon,
                contentDescription = destination.localizedTitle(strings),
                modifier = Modifier
                    .width(78.dp)
                    .height(76.dp)
                    .padding(horizontal = 4.dp, vertical = 3.dp),
                onClick = { onDestinationSelected(destination) }
            )
            Spacer(Modifier.height(HavnSpacing.sm))
        }
    }
}

@Composable
private fun HavnNavigationTile(
    selected: Boolean,
    label: String,
    icon: HavnIconKey,
    contentDescription: String,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    val colors = LocalHavnThemeTokens.current
    val shape = RoundedCornerShape(26.dp)
    val foreground = if (selected) colors.primaryAccent else colors.secondaryText
    Column(
        modifier = modifier
            .clip(shape)
            .clickable(onClick = onClick)
            .padding(horizontal = 4.dp, vertical = 5.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        HavnIcon(
            icon = icon,
            contentDescription = contentDescription,
            modifier = Modifier.size(22.dp),
            tint = foreground
        )
        Spacer(Modifier.height(3.dp))
        Text(
            label,
            color = foreground,
            fontSize = 11.sp,
            lineHeight = 12.sp,
            fontWeight = if (selected) FontWeight.Bold else FontWeight.SemiBold,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun HavnWideSideMenu(
    selected: HavnDestination,
    showAgency: Boolean,
    showProModules: Boolean,
    authState: AuthState,
    authRepository: AuthRepository,
    profileRefreshToken: Int,
    planLabel: String,
    onDestinationSelected: (HavnDestination) -> Unit,
    onUtilitySelected: (HavnDestination) -> Unit,
    onUpgradePlan: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val colors = LocalHavnThemeTokens.current
    val mainItems = buildList {
        add(SideMenuItem.Destination(HavnDestination.Dashboard, icon = HavnIconKey.TodayGrid))
        add(SideMenuItem.Destination(HavnDestination.Properties, icon = HavnIconKey.Home))
        add(SideMenuItem.Destination(HavnDestination.Clients, icon = HavnIconKey.ContactCard))
        add(SideMenuItem.Destination(HavnDestination.OpenHouses, icon = HavnIconKey.CalendarClock))
        add(SideMenuItem.Destination(HavnDestination.Leads, icon = HavnIconKey.Leads))
        if (showProModules) {
            add(SideMenuItem.Destination(HavnDestination.Tasks, icon = HavnIconKey.Check))
            add(SideMenuItem.Destination(HavnDestination.Reports, icon = HavnIconKey.Reports))
            add(SideMenuItem.Destination(HavnDestination.Analytics, icon = HavnIconKey.Chart))
        }
        if (showAgency) {
            add(SideMenuItem.Destination(HavnDestination.Agency, icon = HavnIconKey.Building))
        }
        add(SideMenuItem.Destination(HavnDestination.Settings, icon = HavnIconKey.Settings))
    }
    val userId = (authState as? AuthState.LoggedIn)?.user?.id
    var profileName by remember(userId) { mutableStateOf<String?>(null) }
    var profileAvatarDataUrl by remember(userId) { mutableStateOf<String?>(null) }

    LaunchedEffect(userId, profileRefreshToken) {
        profileName = null
        profileAvatarDataUrl = null
        if (userId != null) {
            authRepository.agentProfile(userId)
                .onSuccess { profile ->
                    profileName = profile.fullName.takeIf { it.isNotBlank() }
                    profileAvatarDataUrl = profile.avatarDataUrl
                }
        }
    }

    val fallbackUserName = when (authState) {
        is AuthState.LoggedIn -> authState.user.email
            ?.substringBefore("@")
            ?.replace(".", " ")
            ?.split(" ")
            ?.filter { it.isNotBlank() }
            ?.joinToString(" ") { segment -> segment.replaceFirstChar { it.uppercase() } }
            ?.takeIf { it.isNotBlank() }
            ?: "Agent"
        else -> "Agent"
    }
    val userName = profileName ?: fallbackUserName

    BoxWithConstraints(
        modifier = Modifier
            .width(252.dp)
            .fillMaxHeight()
            .background(
                Brush.verticalGradient(
                    listOf(
                        colors.surface.copy(alpha = 0.98f),
                        colors.background.copy(alpha = 0.98f)
                    )
                )
            )
            .border(BorderStroke(1.dp, colors.border.copy(alpha = 0.64f)))
            .padding(horizontal = HavnSpacing.md, vertical = HavnSpacing.lg)
    ) {
        val compact = maxHeight < 700.dp
        val veryCompact = maxHeight < 620.dp
        val itemHeight = if (veryCompact) 38.dp else if (compact) 40.dp else 46.dp
        val itemGap = if (veryCompact) 2.dp else if (compact) 4.dp else 8.dp
        val visibleItemCount = when {
            maxHeight < 560.dp -> 7
            maxHeight < 620.dp -> 8
            maxHeight < 700.dp -> 9
            else -> mainItems.size
        }
        val showProfileCard = maxHeight >= 700.dp
        val showSupport = maxHeight >= 980.dp
        val visibleItems = mainItems.visibleFromTopKeepingSelected(
            selected = selected,
            maxCount = visibleItemCount
        )
        Column(Modifier.fillMaxSize()) {
            HavnSidebarLogo(compact = compact)
            Spacer(Modifier.height(if (veryCompact) HavnSpacing.sm else if (compact) HavnSpacing.md else 28.dp))

            visibleItems.forEach { item ->
                val itemDestination = item.destination
                HavnWideSideMenuRow(
                    icon = item.icon,
                    label = item.label(strings),
                    selected = selected == itemDestination,
                    enabled = item.enabled,
                    badge = item.badge,
                    height = itemHeight,
                    onClick = {
                        onDestinationSelected(itemDestination)
                    }
                )
                Spacer(Modifier.height(itemGap))
            }

            Spacer(Modifier.weight(1f))
            if (showProfileCard) {
                HavnSidebarProfileCard(
                    userName = userName,
                    avatarDataUrl = profileAvatarDataUrl,
                    planLabel = planLabel,
                    compact = compact,
                    onUpgradePlan = onUpgradePlan
                )
            }
            if (showSupport) {
                Spacer(Modifier.height(HavnSpacing.lg))
                HavnSidebarSupport()
            }
        }
    }
}

private fun List<SideMenuItem.Destination>.visibleFromTopKeepingSelected(
    selected: HavnDestination,
    maxCount: Int
): List<SideMenuItem.Destination> {
    if (size <= maxCount) return this
    val safeCount = maxCount.coerceIn(1, size)
    val visible = take(safeCount)
    if (visible.any { it.destination == selected }) return visible
    val selectedItem = firstOrNull { it.destination == selected } ?: return visible
    return take(safeCount - 1) + selectedItem
}

@Composable
private fun HavnSidebarLogo(compact: Boolean) {
    val colors = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Image(
            painter = painterResource(Res.drawable.havn_auth_logo),
            contentDescription = "HAVN",
            modifier = Modifier.size(if (compact) 40.dp else 48.dp),
            contentScale = ContentScale.Fit
        )
        Spacer(Modifier.width(HavnSpacing.sm))
        Column {
            Text(
                "HAVN",
                color = colors.primaryText,
                fontSize = if (compact) 24.sp else 28.sp,
                fontWeight = FontWeight.SemiBold,
                letterSpacing = 3.sp
            )
            Text(
                LocalHavnStrings.current.realEstateApp,
                color = colors.primaryAccent,
                style = MaterialTheme.typography.labelSmall
            )
        }
    }
}

@Composable
private fun HavnWideSideMenuRow(
    icon: HavnIconKey,
    label: String,
    selected: Boolean,
    enabled: Boolean,
    badge: String? = null,
    height: androidx.compose.ui.unit.Dp = 46.dp,
    onClick: () -> Unit
) {
    val colors = LocalHavnThemeTokens.current
    val shape = RoundedCornerShape(8.dp)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(height)
            .clip(shape)
            .background(
                Brush.horizontalGradient(
                    if (selected) {
                        listOf(colors.primaryAccent.copy(alpha = 0.18f), colors.card.copy(alpha = 0.18f))
                    } else {
                        listOf(colors.surface.copy(alpha = 0f), colors.surface.copy(alpha = 0f))
                    }
                )
            )
            .then(if (enabled) Modifier.clickable(onClick = onClick) else Modifier)
            .alpha(if (enabled) 1f else 0.54f)
            .padding(horizontal = HavnSpacing.md),
        verticalAlignment = Alignment.CenterVertically
    ) {
        HavnIcon(
            icon = icon,
            contentDescription = label,
            modifier = Modifier.size(22.dp),
            tint = if (selected) colors.primaryAccent else colors.secondaryText
        )
        Spacer(Modifier.width(HavnSpacing.md))
        Text(
            label,
            modifier = Modifier.weight(1f),
            color = if (selected) colors.primaryAccent else colors.secondaryText,
            style = MaterialTheme.typography.bodyMedium,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
        if (badge != null) {
            Box(
                modifier = Modifier
                    .clip(CircleShape)
                    .background(colors.cold.copy(alpha = 0.72f))
                    .padding(horizontal = 10.dp, vertical = 3.dp),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    badge,
                    color = colors.primaryText,
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.Bold
                )
            }
        }
    }
}

@Composable
private fun HavnSidebarProfileCard(
    userName: String,
    avatarDataUrl: String?,
    planLabel: String,
    compact: Boolean,
    onUpgradePlan: () -> Unit
) {
    val colors = LocalHavnThemeTokens.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(colors.card.copy(alpha = 0.72f))
            .border(BorderStroke(1.dp, colors.border.copy(alpha = 0.62f)), RoundedCornerShape(12.dp))
            .padding(if (compact) HavnSpacing.sm else HavnSpacing.md)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(
                modifier = Modifier
                    .size(if (compact) 38.dp else 46.dp)
                    .clip(CircleShape)
                    .background(
                        Brush.linearGradient(
                            listOf(colors.primaryAccent.copy(alpha = 0.82f), colors.secondaryAccent.copy(alpha = 0.62f))
                        )
                    ),
                contentAlignment = Alignment.Center
            ) {
                val rendered = HavnDatabaseImage(
                    dataUrl = avatarDataUrl,
                    contentDescription = userName,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop
                )
                if (!rendered) {
                    HavnIcon(
                        icon = HavnIconKey.User,
                        contentDescription = null,
                        tint = colors.background,
                        modifier = Modifier.size(24.dp)
                    )
                }
            }
            Spacer(Modifier.width(HavnSpacing.sm))
            Column(Modifier.weight(1f)) {
                Text(
                    userName,
                    color = colors.primaryText,
                    style = MaterialTheme.typography.bodyMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Row(verticalAlignment = Alignment.CenterVertically) {
                    HavnIcon(
                        icon = HavnIconKey.Star,
                        contentDescription = null,
                        tint = colors.primaryAccent,
                        modifier = Modifier.size(15.dp)
                    )
                    Spacer(Modifier.width(HavnSpacing.xs))
                    Text(
                        planLabel,
                        color = colors.primaryAccent,
                        style = MaterialTheme.typography.labelMedium
                    )
                }
            }
        }

        if (!compact) {
            Spacer(Modifier.height(HavnSpacing.lg))
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(8.dp))
                    .clickable(onClick = onUpgradePlan)
                    .background(
                        Brush.linearGradient(
                            listOf(colors.primaryAccent.copy(alpha = 0.14f), colors.surface.copy(alpha = 0.42f))
                        )
                    )
                    .border(BorderStroke(1.dp, colors.primaryAccent.copy(alpha = 0.28f)), RoundedCornerShape(8.dp))
                    .padding(HavnSpacing.md)
            ) {
                Text("$planLabel ${LocalHavnStrings.current.planSuffix}", color = colors.primaryText, style = MaterialTheme.typography.bodyMedium)
                Spacer(Modifier.height(HavnSpacing.xs))
                Text(LocalHavnStrings.current.accountReady, color = colors.secondaryText, style = MaterialTheme.typography.labelMedium)
                Spacer(Modifier.height(HavnSpacing.md))
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(36.dp)
                        .clip(RoundedCornerShape(7.dp))
                        .border(BorderStroke(1.dp, colors.primaryAccent.copy(alpha = 0.72f)), RoundedCornerShape(7.dp)),
                    contentAlignment = Alignment.Center
                ) {
                    Text(LocalHavnStrings.current.upgradePlan, color = colors.primaryAccent, style = MaterialTheme.typography.labelMedium)
                }
            }
        }
    }
}

@Composable
private fun HavnSidebarSupport() {
    val colors = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable {
                HavnExternalActions.openEmail(
                    to = "support@havnre.app",
                    subject = "HAVN support",
                    body = ""
                )
            }
            .padding(horizontal = HavnSpacing.md),
        verticalAlignment = Alignment.CenterVertically
    ) {
        HavnIcon(
            icon = HavnIconKey.Phone,
            contentDescription = null,
            modifier = Modifier.size(22.dp),
            tint = colors.secondaryText
        )
        Spacer(Modifier.width(HavnSpacing.md))
        Column {
            Text(LocalHavnStrings.current.needHelp, color = colors.primaryText, style = MaterialTheme.typography.labelMedium)
            Text(LocalHavnStrings.current.contactSupport, color = colors.primaryAccent, style = MaterialTheme.typography.labelMedium)
        }
    }
}

private sealed interface SideMenuItem {
    val icon: HavnIconKey
    val enabled: Boolean
    val badge: String?

    fun label(strings: com.havn.app.core.localization.HavnStrings): String

    data class Destination(
        val destination: HavnDestination,
        override val icon: HavnIconKey = destination.icon
    ) : SideMenuItem {
        override val enabled: Boolean = true
        override val badge: String? = null
        override fun label(strings: com.havn.app.core.localization.HavnStrings): String =
            destination.localizedShortLabel(strings)
    }

    data class Placeholder(
        private val title: String,
        override val icon: HavnIconKey,
        override val badge: String? = null
    ) : SideMenuItem {
        override val enabled: Boolean = false
        override fun label(strings: com.havn.app.core.localization.HavnStrings): String = title
    }
}

@Composable
private fun HavnScreen(
    destination: HavnDestination,
    onLogout: () -> Unit,
    propertiesViewModel: PropertiesViewModel,
    openHousesViewModel: OpenHousesViewModel,
    leadsViewModel: LeadsViewModel,
    clientsViewModel: ClientsViewModel,
    followUpsViewModel: FollowUpsViewModel,
    sellerReportsViewModel: SellerReportsViewModel,
    tasksViewModel: TasksViewModel,
    agencyViewModel: AgencyViewModel,
    photoPicker: HavnPhotoPicker,
    authRepository: AuthRepository,
    languageRepository: com.havn.app.core.localization.HavnLanguageRepository,
    unitPreferencesRepository: com.havn.app.core.settings.HavnUnitPreferencesRepository,
    kioskSecurityRepository: com.havn.app.core.settings.HavnKioskSecurityRepository,
    themeManager: HavnThemeManager,
    notificationRepository: NotificationRepository,
    billingService: HavnBillingService,
    canUseProModules: Boolean,
    agentProfileRefreshToken: Int,
    onAgentProfileChanged: () -> Unit,
    leadReturnOpeningId: String?,
    onLeadReturnOpeningChanged: (String?) -> Unit,
    contactReturnClientId: String?,
    contactReturnLabel: String?,
    onContactReturnChanged: (String?, String?) -> Unit,
    openingReturnToToday: Boolean,
    onOpeningReturnToTodayChanged: (Boolean) -> Unit,
    onDestinationSelected: (HavnDestination) -> Unit,
    showDashboardHeaderActions: Boolean = true,
    openSettingsPurchase: Boolean = false,
    onSettingsPurchaseConsumed: () -> Unit = {},
    onOpenSubscription: () -> Unit = {}
) {
    val agencyState by agencyViewModel.uiState.collectAsState()
    val currentAuthState by authRepository.authState.collectAsState()
    val sellerReportsState by sellerReportsViewModel.uiState.collectAsState()
    val currentUser = (currentAuthState as? AuthState.LoggedIn)?.user
    var agentProfileName by remember(currentUser?.id) { mutableStateOf<String?>(null) }
    val canUseAiFollowUps = agencyState.entitlement.canUseAiFollowUps ||
        agencyState.entitlement.tier == HavnSubscriptionTier.SoloAgent ||
        agencyState.entitlement.tier == HavnSubscriptionTier.ProAgent
    val phoneBackToToday: (() -> Unit)? = if (!havnIsWebShell && !havnIsTabletDevice()) {
        { onDestinationSelected(HavnDestination.Dashboard) }
    } else {
        null
    }
    LaunchedEffect(currentUser?.id, agentProfileRefreshToken) {
        agentProfileName = null
        currentUser?.id?.let { userId ->
            authRepository.agentProfile(userId)
                .onSuccess { profile ->
                    agentProfileName = profile.fullName.takeIf { it.isNotBlank() }
                }
        }
    }
    val selectedAgency = agencyState.selectedAgency ?: agencyState.agency
    val agencyMemberName = agencyState.members
        .firstOrNull { it.agentId == currentUser?.id }
        ?.fullName
        ?.takeIf { it.isNotBlank() }
    val fallbackAgentName = currentUser?.email
        ?.substringBefore("@")
        ?.replace(".", " ")
        ?.split(" ")
        ?.filter { it.isNotBlank() }
        ?.joinToString(" ") { segment -> segment.replaceFirstChar { it.uppercase() } }
    val kioskBranding = KioskBranding(
        agencyName = selectedAgency?.name,
        agencyIconUrl = selectedAgency?.iconUrl,
        agentName = agentProfileName ?: agencyMemberName ?: fallbackAgentName,
        useAgencyBranding = agencyState.entitlement.isProAgent
    )

    when (destination) {
        HavnDestination.Auth,
        HavnDestination.Dashboard -> DashboardScreen(
            authRepository = authRepository,
            agencyViewModel = agencyViewModel,
            propertiesViewModel = propertiesViewModel,
            openHousesViewModel = openHousesViewModel,
            leadsViewModel = leadsViewModel,
            followUpsViewModel = followUpsViewModel,
            sellerReportsViewModel = sellerReportsViewModel,
            onOpenOpening = { openHouseId ->
                onContactReturnChanged(null, null)
                onOpeningReturnToTodayChanged(true)
                openHousesViewModel.openDetail(openHouseId)
                onDestinationSelected(HavnDestination.OpenHouses)
            },
            onViewProperty = { propertyId ->
                propertiesViewModel.openProperty(propertyId)
                onDestinationSelected(HavnDestination.Properties)
            },
            onOpenLeads = {
                onLeadReturnOpeningChanged(null)
                onDestinationSelected(HavnDestination.Leads)
            },
            onOpenProperties = {
                onDestinationSelected(HavnDestination.Properties)
            },
            onOpenReports = {
                if (agencyState.entitlement.isProAgent) {
                    sellerReportsViewModel.openList()
                    onDestinationSelected(HavnDestination.Reports)
                } else {
                    onOpenSubscription()
                }
            },
            onOpenAnalytics = {
                if (agencyState.entitlement.isProAgent) onDestinationSelected(HavnDestination.Analytics) else onOpenSubscription()
            },
            onOpenTasks = {
                if (agencyState.entitlement.isProAgent) onDestinationSelected(HavnDestination.Tasks) else onOpenSubscription()
            },
            onAddProperty = {
                propertiesViewModel.startAdd()
                onDestinationSelected(HavnDestination.Properties)
            },
            onScheduleOpenHouse = { propertyId ->
                onContactReturnChanged(null, null)
                onOpeningReturnToTodayChanged(false)
                openHousesViewModel.showScheduleForProperty(propertyId)
                onDestinationSelected(HavnDestination.OpenHouses)
            },
            onOpenSettings = {
                onDestinationSelected(HavnDestination.Settings)
            },
            onOpenAgency = {
                onDestinationSelected(HavnDestination.Agency)
            },
            showHeaderActions = showDashboardHeaderActions
        )
        HavnDestination.Properties -> PropertiesScreen(
            viewModel = propertiesViewModel,
            photoPicker = photoPicker,
            propertyOpenings = openHousesViewModel.uiState.collectAsState().value.openHouses,
            generatingSellerReportPropertyId = sellerReportsState.generatingPropertyId,
            onOpenPropertyDetail = { propertyId ->
                openHousesViewModel.loadOpenHousesForProperty(propertyId)
            },
            onScheduleOpenHouse = { propertyId ->
                onContactReturnChanged(null, null)
                onOpeningReturnToTodayChanged(false)
                openHousesViewModel.showScheduleForProperty(propertyId)
                onDestinationSelected(HavnDestination.OpenHouses)
            },
            onOpenOpening = { openingId ->
                onContactReturnChanged(null, null)
                onOpeningReturnToTodayChanged(false)
                openHousesViewModel.openDetail(openingId)
                onDestinationSelected(HavnDestination.OpenHouses)
            },
            onOpenOwnerContact = { clientId ->
                onContactReturnChanged(null, null)
                onOpeningReturnToTodayChanged(false)
                clientsViewModel.openClient(clientId)
                onDestinationSelected(HavnDestination.Clients)
            },
            canGenerateSellerReport = agencyState.entitlement.isProAgent,
            onGenerateSellerReport = { propertyId ->
                if (agencyState.entitlement.isProAgent) {
                    sellerReportsViewModel.generatePropertyReport(propertyId)
                    onDestinationSelected(HavnDestination.Reports)
                } else {
                    onOpenSubscription()
                }
            }
        )
        HavnDestination.OpenHouses -> OpenHousesScreen(
            viewModel = openHousesViewModel,
            kioskPin = kioskSecurityRepository.pin.collectAsState().value,
            kioskBranding = kioskBranding,
            returnToToday = openingReturnToToday,
            returnToContactLabel = contactReturnLabel?.takeIf { contactReturnClientId != null },
            onReturnToToday = {
                onOpeningReturnToTodayChanged(false)
                onContactReturnChanged(null, null)
                onDestinationSelected(HavnDestination.Dashboard)
            },
            onReturnToProperty = { propertyId ->
                onOpeningReturnToTodayChanged(false)
                onContactReturnChanged(null, null)
                propertiesViewModel.openProperty(propertyId)
                onDestinationSelected(HavnDestination.Properties)
            },
            onReturnToContact = contactReturnClientId?.let { clientId ->
                {
                    onOpeningReturnToTodayChanged(false)
                    onContactReturnChanged(null, null)
                    clientsViewModel.openClient(clientId)
                    onDestinationSelected(HavnDestination.Clients)
                }
            } ?: {},
            onOpenLead = { leadId, openingId ->
                onLeadReturnOpeningChanged(openingId)
                onContactReturnChanged(null, null)
                leadsViewModel.openLead(leadId)
                onDestinationSelected(HavnDestination.Leads)
            }
        )
        HavnDestination.Clients -> ClientsScreen(
            viewModel = clientsViewModel,
            photoPicker = photoPicker,
            onOpenOpening = { openingId ->
                val clientsState = clientsViewModel.uiState.value
                val selectedClient = clientsState.selectedClient
                onContactReturnChanged(
                    clientsState.selectedClientId,
                    selectedClient?.fullName?.takeIf { it.isNotBlank() } ?: "Contact"
                )
                onOpeningReturnToTodayChanged(false)
                openHousesViewModel.openDetail(openingId)
                onDestinationSelected(HavnDestination.OpenHouses)
            }
        )
        HavnDestination.Leads -> LeadsScreen(
            viewModel = leadsViewModel,
            followUpsViewModel = followUpsViewModel,
            onSendFollowUp = { leadId ->
                followUpsViewModel.openSendFollowUpForLead(leadId)
                onDestinationSelected(HavnDestination.FollowUps)
            },
            onFollowUpCompleted = {
                leadsViewModel.refresh()
            },
            canUseAiFollowUps = canUseAiFollowUps,
            onAiUpgradeRequired = onOpenSubscription,
            detailBackLabel = leadReturnOpeningId?.let { "Opening" },
            onBackFromDetail = leadReturnOpeningId?.let { openingId ->
                {
                    leadsViewModel.closeDetail()
                    openHousesViewModel.showExistingDetail(openingId)
                    onLeadReturnOpeningChanged(null)
                    onDestinationSelected(HavnDestination.OpenHouses)
                }
            }
        )
        HavnDestination.FollowUps -> FollowUpsScreen(
            viewModel = followUpsViewModel,
            onBackToLeadDetail = { onDestinationSelected(HavnDestination.Leads) },
            onFollowUpCompleted = {
                leadsViewModel.refresh()
            },
            canUseAiFollowUps = canUseAiFollowUps,
            onAiUpgradeRequired = onOpenSubscription
        )
        HavnDestination.Reports -> if (canUseProModules) {
            SellerReportsScreen(
                viewModel = sellerReportsViewModel,
                onBackToToday = phoneBackToToday
            )
        } else {
            SettingsScreen(
                authRepository = authRepository,
                agencyViewModel = agencyViewModel,
                languageRepository = languageRepository,
                unitPreferencesRepository = unitPreferencesRepository,
                kioskSecurityRepository = kioskSecurityRepository,
                themeManager = themeManager,
                notificationRepository = notificationRepository,
                billingService = billingService,
                photoPicker = photoPicker,
                openPurchaseOnStart = true,
                onPurchaseStartConsumed = {},
                onLogout = onLogout,
                onAgentProfileChanged = onAgentProfileChanged
            )
        }
        HavnDestination.Tasks -> if (canUseProModules) {
            TasksScreen(
                viewModel = tasksViewModel,
                propertiesViewModel = propertiesViewModel,
                openHousesViewModel = openHousesViewModel,
                leadsViewModel = leadsViewModel,
                followUpsViewModel = followUpsViewModel,
                agencyViewModel = agencyViewModel,
                onBackToToday = phoneBackToToday
            )
        } else {
            SettingsScreen(
                authRepository = authRepository,
                agencyViewModel = agencyViewModel,
                languageRepository = languageRepository,
                unitPreferencesRepository = unitPreferencesRepository,
                kioskSecurityRepository = kioskSecurityRepository,
                themeManager = themeManager,
                notificationRepository = notificationRepository,
                billingService = billingService,
                photoPicker = photoPicker,
                openPurchaseOnStart = true,
                onPurchaseStartConsumed = {},
                onLogout = onLogout,
                onAgentProfileChanged = onAgentProfileChanged
            )
        }
        HavnDestination.Analytics -> if (canUseProModules) {
            AnalyticsScreen(
                propertiesViewModel = propertiesViewModel,
                openHousesViewModel = openHousesViewModel,
                leadsViewModel = leadsViewModel,
                followUpsViewModel = followUpsViewModel,
                agencyViewModel = agencyViewModel,
                onBackToToday = phoneBackToToday
            )
        } else {
            SettingsScreen(
                authRepository = authRepository,
                agencyViewModel = agencyViewModel,
                languageRepository = languageRepository,
                unitPreferencesRepository = unitPreferencesRepository,
                kioskSecurityRepository = kioskSecurityRepository,
                themeManager = themeManager,
                notificationRepository = notificationRepository,
                billingService = billingService,
                photoPicker = photoPicker,
                openPurchaseOnStart = true,
                onPurchaseStartConsumed = {},
                onLogout = onLogout,
                onAgentProfileChanged = onAgentProfileChanged
            )
        }
        HavnDestination.Agency -> {
            val propertiesState by propertiesViewModel.uiState.collectAsState()
            val leadsState by leadsViewModel.uiState.collectAsState()
            AgencyScreen(
                viewModel = agencyViewModel,
                photoPicker = photoPicker,
                propertyCount = propertiesState.totalPropertyCount,
                leadCount = leadsState.leads.size,
                recentLeadTitle = leadsState.leads.firstOrNull()?.sourceLine,
                recentPropertyTitle = propertiesState.properties.firstOrNull()?.title,
                onBack = { onDestinationSelected(HavnDestination.Dashboard) },
                onManageAgency = { onDestinationSelected(HavnDestination.Settings) }
            )
        }
        HavnDestination.Settings -> SettingsScreen(
            authRepository = authRepository,
            agencyViewModel = agencyViewModel,
            languageRepository = languageRepository,
            unitPreferencesRepository = unitPreferencesRepository,
            kioskSecurityRepository = kioskSecurityRepository,
            themeManager = themeManager,
            notificationRepository = notificationRepository,
            billingService = billingService,
            photoPicker = photoPicker,
            openPurchaseOnStart = openSettingsPurchase,
            onPurchaseStartConsumed = onSettingsPurchaseConsumed,
            onAgentProfileChanged = onAgentProfileChanged,
            onBack = { onDestinationSelected(HavnDestination.Dashboard) },
            onLogout = onLogout
        )
    }
}

private fun resetDestinationToRoot(
    destination: HavnDestination,
    propertiesViewModel: PropertiesViewModel,
    openHousesViewModel: OpenHousesViewModel,
    leadsViewModel: LeadsViewModel,
    clientsViewModel: ClientsViewModel,
    followUpsViewModel: FollowUpsViewModel,
    sellerReportsViewModel: SellerReportsViewModel
) {
    when (destination) {
        HavnDestination.Properties -> propertiesViewModel.closeDetail()
        HavnDestination.OpenHouses -> openHousesViewModel.showList()
        HavnDestination.Clients -> clientsViewModel.openList()
        HavnDestination.Leads -> leadsViewModel.closeDetail()
        HavnDestination.FollowUps -> followUpsViewModel.openQueue()
        HavnDestination.Reports -> sellerReportsViewModel.openList()
        HavnDestination.Tasks -> Unit
        HavnDestination.Analytics -> Unit
        HavnDestination.Auth,
        HavnDestination.Dashboard,
        HavnDestination.Agency,
        HavnDestination.Settings -> Unit
    }
}

private fun routeHavnDeepLink(
    link: String,
    propertiesViewModel: PropertiesViewModel,
    openHousesViewModel: OpenHousesViewModel,
    leadsViewModel: LeadsViewModel,
    sellerReportsViewModel: SellerReportsViewModel,
    onDestinationSelected: (HavnDestination) -> Unit,
    onOpenSubscription: () -> Unit
) {
    val normalized = link.removePrefix("havn://").trim('/')
    val parts = normalized.split('/').filter { it.isNotBlank() }
    val id = parts.getOrNull(1) ?: return
    when (parts.firstOrNull()) {
        "lead" -> {
            leadsViewModel.openLead(id)
            onDestinationSelected(HavnDestination.Leads)
        }
        "property" -> {
            propertiesViewModel.openProperty(id)
            onDestinationSelected(HavnDestination.Properties)
        }
        "open-house" -> {
            openHousesViewModel.openDetail(id)
            onDestinationSelected(HavnDestination.OpenHouses)
        }
        "report" -> {
            sellerReportsViewModel.openList()
            onDestinationSelected(HavnDestination.Reports)
        }
        "agency" -> onDestinationSelected(HavnDestination.Agency)
        "settings" -> onOpenSubscription()
    }
}

private fun HavnDestination.navigationDepth(): Int = when (this) {
    HavnDestination.Auth -> 0
    HavnDestination.Dashboard -> 0
    HavnDestination.Properties -> 1
    HavnDestination.OpenHouses -> 2
    HavnDestination.Clients -> 3
    HavnDestination.Leads -> 4
    HavnDestination.FollowUps -> 5
    HavnDestination.Reports -> 5
    HavnDestination.Tasks -> 5
    HavnDestination.Analytics -> 5
    HavnDestination.Agency -> 5
    HavnDestination.Settings -> 5
}

private val HavnDestination.isProModule: Boolean
    get() = this == HavnDestination.Tasks ||
        this == HavnDestination.Reports ||
        this == HavnDestination.Analytics

private val HavnSubscriptionTier.displayLabel: String
    get() = when (this) {
        HavnSubscriptionTier.Free -> "Free"
        HavnSubscriptionTier.SoloAgent -> "Solo Agent"
        HavnSubscriptionTier.ProAgent -> "Pro Agent"
    }
